// Housekeeping (docs/plans/phase3-kitchen-and-housekeeping.md §2.3). No patient data; notes
// travel in bodies only.
import { queryOptions } from '@tanstack/react-query';
import {
  consumableOptionsSchema,
  housekeepingAreasSchema,
  housekeepingTaskListSchema,
  housekeepingTaskSchema,
  type AddConsumableRequest,
  type SaveAreaRequest,
  type SaveScheduleRequest,
} from '@wodi/shared';

import { api } from './client.ts';

export const housekeepingKeys = {
  all: ['housekeeping'] as const,
  areas: ['housekeeping', 'areas'] as const,
  beds: ['housekeeping', 'beds'] as const,
  overdue: ['housekeeping', 'overdue'] as const,
  area: (areaId: string, date: string) => ['housekeeping', 'area', areaId, date] as const,
  consumables: (storeId: string | null) =>
    ['housekeeping', 'consumables', storeId ?? 'none'] as const,
};

export const housekeepingAreasQuery = queryOptions({
  queryKey: housekeepingKeys.areas,
  queryFn: () => api('GET', '/housekeeping/areas', { schema: housekeepingAreasSchema }),
});

/** Beds waiting for cleaning refresh every 30 seconds, like the bed board. */
export const bedCleaningQuery = queryOptions({
  queryKey: housekeepingKeys.beds,
  queryFn: () => api('GET', '/housekeeping/beds', { schema: housekeepingTaskListSchema }),
  refetchInterval: 30_000,
});

export const overdueTasksQuery = queryOptions({
  queryKey: housekeepingKeys.overdue,
  queryFn: () => api('GET', '/housekeeping/overdue', { schema: housekeepingTaskListSchema }),
  refetchInterval: 60_000,
});

export function areaTasksQuery(areaId: string, date: string) {
  return queryOptions({
    queryKey: housekeepingKeys.area(areaId, date),
    queryFn: () =>
      api('GET', `/housekeeping/tasks?areaId=${areaId}&date=${date}`, {
        schema: housekeepingTaskListSchema,
      }),
  });
}

export function consumableOptionsQuery(storeId: string | null) {
  return queryOptions({
    queryKey: housekeepingKeys.consumables(storeId),
    queryFn: () =>
      api(
        'GET',
        storeId === null
          ? '/housekeeping/consumable-options'
          : `/housekeeping/consumable-options?storeId=${storeId}`,
        { schema: consumableOptionsSchema },
      ),
  });
}

export function startTask(taskId: string) {
  return api('POST', `/housekeeping/tasks/${taskId}/start`, { schema: housekeepingTaskSchema });
}

export function completeTask(taskId: string, note: string | null) {
  return api('POST', `/housekeeping/tasks/${taskId}/complete`, {
    body: { note },
    schema: housekeepingTaskSchema,
  });
}

export function skipTask(taskId: string, reason: string) {
  return api('POST', `/housekeeping/tasks/${taskId}/skip`, {
    body: { reason },
    schema: housekeepingTaskSchema,
  });
}

export function addConsumable(taskId: string, body: AddConsumableRequest) {
  return api('POST', `/housekeeping/tasks/${taskId}/consumables`, {
    body,
    schema: housekeepingTaskSchema,
  });
}

export function createArea(body: SaveAreaRequest) {
  return api('POST', '/housekeeping/areas', { body, schema: housekeepingAreasSchema });
}

export function updateArea(areaId: string, body: SaveAreaRequest) {
  return api('PATCH', `/housekeeping/areas/${areaId}`, { body, schema: housekeepingAreasSchema });
}

export function createSchedule(areaId: string, body: SaveScheduleRequest) {
  return api('POST', `/housekeeping/areas/${areaId}/schedules`, {
    body,
    schema: housekeepingAreasSchema,
  });
}

export function updateSchedule(scheduleId: string, body: SaveScheduleRequest) {
  return api('PATCH', `/housekeeping/schedules/${scheduleId}`, {
    body,
    schema: housekeepingAreasSchema,
  });
}
